import {pick} from '@benchling/js-utils';

import {ArchiveRecord} from 'archive/ArchiveConstants';
import * as benchlingUtil from 'BenchlingUtil';
import {writeFragmentToCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import notifications from 'notifications';
import {Thunk} from 'redux-stores/Store';
import {RequestTaskSchema} from 'requests/tasks/RequestTaskConstants';
import {SchemaSpec} from 'schemas/spec/SchemaConfigConstants';
import * as SchemaConfigReduxActions from 'schemas/spec/SchemaConfigReduxActions';
import {withLoadingScreen} from 'util/Promises';

import * as RequestTaskSchemaDAO from './RequestTaskSchemaDAO';

export enum RequestTaskSchemaActionType {
  REQUEST_TASK_SCHEMAS_FETCHED = 'REQUEST_TASK_SCHEMAS_FETCHED',
  REQUEST_TASK_SCHEMA_FETCHED = 'REQUEST_TASK_SCHEMA_FETCHED',
  REQUEST_TASK_SCHEMAS_ARCHIVED = 'REQUEST_TASK_SCHEMAS_ARCHIVED',
  REQUEST_TASK_SCHEMAS_UNARCHIVED = 'REQUEST_TASK_SCHEMAS_UNARCHIVED',
  REQUEST_TASK_SCHEMAS_UPDATED_FROM_GQL = 'REQUEST_TASK_SCHEMAS_UPDATED_FROM_GQL',
}

interface RequestTaskSchemasArchivedAction {
  type: RequestTaskSchemaActionType.REQUEST_TASK_SCHEMAS_ARCHIVED;
  itemIds: Array<string>;
  archiveRecordsByItemId: {
    [schemaId: string]: ArchiveRecord;
  };
}

export function requestTaskSchemasArchived(
  itemIds: Array<string>,
  archiveRecordsByItemId: {[schemaId: string]: ArchiveRecord}
): RequestTaskSchemasArchivedAction {
  return {
    type: RequestTaskSchemaActionType.REQUEST_TASK_SCHEMAS_ARCHIVED,
    itemIds,
    archiveRecordsByItemId,
  };
}

interface RequestTaskSchemasUnarchivedAction {
  type: RequestTaskSchemaActionType.REQUEST_TASK_SCHEMAS_UNARCHIVED;
  itemIds: Array<string>;
}

export function requestTaskSchemasUnarchived(itemIds: Array<string>): RequestTaskSchemasUnarchivedAction {
  return {
    type: RequestTaskSchemaActionType.REQUEST_TASK_SCHEMAS_UNARCHIVED,
    itemIds,
  };
}

interface RequestTaskSchemasFetchedAction {
  type: RequestTaskSchemaActionType.REQUEST_TASK_SCHEMAS_FETCHED;
  requestTaskSchemasById: {[schemaId: string]: RequestTaskSchema};
}

export function requestTaskSchemasFetched(requestTaskSchemasById: {
  [schemaId: string]: RequestTaskSchema;
}): RequestTaskSchemasFetchedAction {
  return {
    type: RequestTaskSchemaActionType.REQUEST_TASK_SCHEMAS_FETCHED,
    requestTaskSchemasById,
  };
}

interface RequestTaskSchemaFetchedAction {
  type: RequestTaskSchemaActionType.REQUEST_TASK_SCHEMA_FETCHED;
  requestTaskSchema: RequestTaskSchema;
}

function requestTaskSchemaFetched(requestTaskSchema: RequestTaskSchema): RequestTaskSchemaFetchedAction {
  return {
    type: RequestTaskSchemaActionType.REQUEST_TASK_SCHEMA_FETCHED,
    requestTaskSchema,
  };
}

export function fetchAll(): Thunk<Promise<void>> {
  return async (dispatch) => {
    const {requestTaskSchemasById} = await RequestTaskSchemaDAO.fetchAll();
    dispatch(requestTaskSchemasFetched(requestTaskSchemasById));
  };
}

interface RequestTaskSchemasUpdatedFromGQLAction {
  type: RequestTaskSchemaActionType.REQUEST_TASK_SCHEMAS_UPDATED_FROM_GQL;
  requestTaskSchemasById: {[schemaId: string]: RequestTaskSchema};
}

export function requestTaskSchemasUpdatedFromGQL(requestTaskSchemasById: {
  [schemaId: string]: RequestTaskSchema;
}): RequestTaskSchemasUpdatedFromGQLAction {
  return {type: RequestTaskSchemaActionType.REQUEST_TASK_SCHEMAS_UPDATED_FROM_GQL, requestTaskSchemasById};
}

export function fetchSchemaSpec(schemaId: string): Thunk<void> {
  return async (dispatch) => {
    const {requestTaskSchema, schemaSpec} = await RequestTaskSchemaDAO.fetchSchemaSpec(schemaId);
    dispatch(requestTaskSchemaFetched(requestTaskSchema));
    dispatch(SchemaConfigReduxActions.schemaSpecFetched(requestTaskSchema.id, schemaSpec));
  };
}

export function updateSchemaSpec({
  schemaId,
  schemaName,
  spec,
}: {
  schemaId: string;
  schemaName: string;
  spec: SchemaSpec;
}): Thunk<void> {
  return async (dispatch) => {
    try {
      const {requestTaskSchema, schemaSpec} = await withLoadingScreen(
        RequestTaskSchemaDAO.updateSchemaSpec(schemaId, schemaName, spec),
        'Updating...'
      );
      dispatch(requestTaskSchemaFetched(requestTaskSchema));
      writeFragmentToCache(getFragmentWrapper('RequestTaskSchema'), {
        __typename: 'RequestTaskSchemaNode',
        ...pick(requestTaskSchema, 'id', 'name', 'sqlIdentifier'),
        allFieldsJson: requestTaskSchema.allFields,
        fieldsJson: requestTaskSchema.fields,
        organizationJson: requestTaskSchema.organization,
        archiveRecord:
          requestTaskSchema.archiveRecord != null
            ? {__typename: 'ArchiveRecordNode', purpose: requestTaskSchema.archiveRecord.purpose}
            : null,
      });
      dispatch(SchemaConfigReduxActions.schemaSpecFetched(requestTaskSchema.id, schemaSpec));
      notifications.success('Updated schema successfully');
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (response) {
      dispatch(SchemaConfigReduxActions.handleSchemaSpecError(response));
    }
  };
}

export function createSchema({
  ownerId,
  name,
  spec,
}: {
  ownerId: string;
  name: string;
  spec: SchemaSpec;
}): Thunk<void> {
  return async (dispatch) => {
    try {
      const {requestTaskSchema} = await RequestTaskSchemaDAO.createSchema(ownerId, name, spec);
      dispatch(requestTaskSchemaFetched(requestTaskSchema));
      dispatch(SchemaConfigReduxActions.schemaSpecFetched(requestTaskSchema.id, spec));
      benchlingUtil.navigate(null, RequestTaskSchemaDAO.taskSchemaURL(requestTaskSchema.id));
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (response) {
      dispatch(SchemaConfigReduxActions.handleSchemaSpecError(response));
    }
  };
}

export type RequestTaskSchemaAction =
  | RequestTaskSchemasFetchedAction
  | RequestTaskSchemaFetchedAction
  | RequestTaskSchemasArchivedAction
  | RequestTaskSchemasUnarchivedAction
  | RequestTaskSchemasUpdatedFromGQLAction;
